/**
 * POC animated lobby — the EffectsLayer.
 *
 * Fixed, full-viewport, pointer-transparent, portaled into #lobby2-root (above the grid and the
 * bars, below the body-level modals). Mounted only while the effects bus has something active and
 * unmounted when the queue drains: at rest there is no `[data-effects-layer]` node and no canvas.
 * The canvas renderer is a separate chunk, requested the first time a canvas effect plays.
 */

import React, { lazy, Suspense, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { EffectInstance } from './effectsBus';
import { effectsBus, isCanvasEffect } from './effectsBus';
import { AmountBurst, GameOpenTunnel, RevealCurtain } from './domEffects';
import styles from './EffectsLayer.module.css';
import './fx.css';

const CanvasEffects = lazy(() => import(/* webpackChunkName: "fx-canvas" */ './canvas/CanvasEffects'));

const PORTAL_TARGET_ID = 'lobby2-root';

const renderDomEffect = (effect: EffectInstance): React.ReactNode => {
  switch (effect.name) {
    case 'reveal':
      return <RevealCurtain key={effect.id} id={effect.id} />;
    case 'gameOpen':
      return <GameOpenTunnel key={effect.id} id={effect.id} rect={effect.payload.rect} />;
    case 'amountBurst':
      return <AmountBurst key={effect.id} id={effect.id} amount={effect.payload.amount} label={effect.payload.label} />;
    default:
      return null;
  }
};

const EffectsLayer: React.FC = () => {
  const [active, setActive] = useState<readonly EffectInstance[]>(effectsBus.getActive);

  useEffect(() => {
    effectsBus.setLayerMounted(true);
    const unsubscribe = effectsBus.subscribe(setActive);
    // Anything played between the first render and the subscription.
    setActive(effectsBus.getActive());
    return () => {
      unsubscribe();
      effectsBus.setLayerMounted(false);
    };
  }, []);

  if (active.length === 0) return null;

  const canvasEffects = active.filter(isCanvasEffect);
  const target = document.getElementById(PORTAL_TARGET_ID) ?? document.body;

  return createPortal(
    <div className={styles.layer} data-effects-layer="" aria-hidden="true">
      {active.map(renderDomEffect)}
      {canvasEffects.length > 0 && (
        <Suspense fallback={null}>
          <CanvasEffects effects={canvasEffects} />
        </Suspense>
      )}
    </div>,
    target,
  );
};

export default EffectsLayer;
